import { z } from 'zod'
import { MessagingSettings } from './messaging'
import { JourneyOptions, JourneyStep, JourneyTrigger } from './journeys'
import { Channel, QuestionType, TaskRoute } from './enums'
import { ObjectTypeInput } from './fields'
import { AutonomyPolicy } from './trust'
import { RequiredWhen } from './requiredWhen'

/**
 * Everything that shapes AI behaviour is configuration, and every piece of
 * configuration changes through the same pattern: draft, test, approve,
 * release, roll back (design 15.1). These schemas are the single definition
 * of each configurable entity, used by the API, the settings assistant and
 * the console.
 */

// --- Decision questions (design 9.1) ----------------------------------------
export const QuestionOutcome = z.object({
  action: z.enum(['exception', 'start_task', 'lower_autonomy', 'hold', 'tag', 'ignore', 'live_handover']),
  reason: z.string().default(''),
  reason_code: z.string().optional(),
  priority: z.number().int().min(1).max(5).default(3),
  task_kind: z.enum(['coding', 'business']).optional(),
  tag: z.string().optional(),
  guidance: z.string().optional(),
  /**
   * A fixed, pre-approved message sent to the customer at once when the question fires, before anything else
   * (e.g. "safety first": on a gas smell, sparks or flooding, how to make it safe and the emergency numbers), even
   * when the reply itself goes to a person. Never written by the model.
   */
  reply: z.string().trim().min(1).max(1000).optional(),
})
export type QuestionOutcome = z.infer<typeof QuestionOutcome>

export const QuestionRule = z.object({
  pattern: z.string(),            // case-insensitive regular expression
  answer: z.string(),             // the answer this rule gives when the pattern matches
})
export type QuestionRule = z.infer<typeof QuestionRule>

export const DecisionQuestionInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  layer: z.enum(['platform', 'pack', 'company']).default('company'),
  locked: z.boolean().default(false),
  type: QuestionType,
  label: z.string().min(1),
  instructions: z.string().min(5),
  // noul: {true: meaning, false: meaning}; choice: {option: meaning}; score: [level meanings, lowest first]
  criteria: z.union([z.record(z.string(), z.string()), z.array(z.string())]),
  // noul: ["true"]; choice: options that fire; score: [min level index as string]
  fires_on: z.array(z.string()).min(1),
  threshold: z.number().min(0).max(1).default(0.6),
  unsure_low: z.number().min(0).max(1).default(0.45),
  unsure_high: z.number().min(0).max(1).default(0.75),
  outcome: QuestionOutcome,
  applies_to: z.array(z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp', 'task'])).default(['email', 'sms', 'chat', 'voice', 'task']),
  rules: z.array(QuestionRule).default([]),
  enabled: z.boolean().default(true),
})
export type DecisionQuestionInput = z.infer<typeof DecisionQuestionInput>
export type DecisionQuestion = DecisionQuestionInput & { id?: string; version: number }

// --- Playbooks (design 8.4) --------------------------------------------------
export const PlaybookAction = z.object({
  action: z.string(),             // a registered action key, e.g. refund, waive_fee, move_booking
  limit_minor: z.number().int().nonnegative().optional(),
  autonomy: z.enum(['never', 'approve', 'alone']).default('approve'),
  note: z.string().optional(),
  /** Frequency limit (design 8.4, mockup m7 "twice per booking", "once a year"): at most this many runs... */
  max_count: z.number().int().positive().optional(),
  /** ...per booking (the record the action changes) or per customer... */
  per: z.enum(['record', 'customer']).optional(),
  /** ...within this many days (no period: ever). Over the limit, the action needs a person's approval. */
  period_days: z.number().int().positive().optional(),
})
export type PlaybookAction = z.infer<typeof PlaybookAction>

export const PlaybookInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  name: z.string().min(1),
  layer: z.enum(['platform', 'pack', 'company', 'site']).default('company'),
  goal: z.string().min(1),
  steps: z.array(z.string()).default([]),
  actions: z.array(PlaybookAction).default([]),
  handover: z.array(z.string()).default([]),
  channel_notes: z.record(z.string(), z.string()).default({}),
  approved_wording: z.record(z.string(), z.string()).default({}),
  examples: z.array(z.string()).default([]),
})
export type PlaybookInput = z.infer<typeof PlaybookInput>

// --- Knowledge (design 8.3) -------------------------------------------------
export const KnowledgeInput = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  scope: z.object({ sites: z.array(z.string()).optional(), channels: z.array(Channel).optional() }).default({}),
  review_at: z.string().optional(),
  /** The staff member who keeps the article true; reminded when its review is due (design 8.3). */
  owner_id: z.string().uuid().optional(),
  /** Where an ingested article came from (a website page or a file), for re-sync and freshness. */
  source_id: z.string().uuid().optional(),
  source_url: z.string().optional(),
  content_hash: z.string().optional(),
})
export type KnowledgeInput = z.infer<typeof KnowledgeInput>

// --- Task templates (design 10.6) -------------------------------------------
export const RequiredInfo = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  label: z.string(),
  description: z.string().default(''),
  example: z.string().optional(),
  optional: z.boolean().default(false),
  /** Needed only when another item has a value ("PO number when the customer is corporate", F 15.3-03); overrides `optional`. */
  required_when: RequiredWhen.optional(),
})
export type RequiredInfo = z.infer<typeof RequiredInfo>

export const TaskTemplateInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  name: z.string().min(1),
  kind: z.enum(['coding', 'business']),
  description: z.string().default(''),
  required_info: z.array(RequiredInfo).default([]),
  systems: z.object({
    repos: z.array(z.string()).default([]),
    network: z.array(z.string()).default([]),
    crm_objects: z.array(z.string()).default([]),
    actions: z.array(z.string()).default([]),
  }).default({ repos: [], network: [], crm_objects: [], actions: [] }),
  risk: z.enum(['low', 'medium', 'high']).default('low'),
  default_route: TaskRoute.default('ai_signoff'),
  acceptance: z.array(z.string()).default([]),
  gate: z.enum(['none', 'customer_preview', 'consultant_review', 'both']).default('customer_preview'),
  price_credits: z.number().int().nonnegative().default(0),
  budget: z.object({
    minutes: z.number().int().positive().default(30),
    cost_micros: z.number().int().positive().default(2_000_000),
    max_files: z.number().int().positive().default(20),
  }).default({ minutes: 30, cost_micros: 2_000_000, max_files: 20 }),
  examples: z.array(z.string()).default([]),
})
export type TaskTemplateInput = z.infer<typeof TaskTemplateInput>

// --- Journeys (design 14) ----------------------------------------------------
/**
 * What a journey is for decides which consent it needs at the send gate (design 14, 17.5):
 * - service: about something the customer has (a booking, an order). No opt-in needed; an opt-out is honoured.
 * - collections: about money owed under a contract. No opt-in needed; an opt-out is honoured and recorded.
 * - marketing: selling. Needs consent granted for marketing on that channel.
 */
export const JOURNEY_PURPOSES = ['service', 'collections', 'marketing'] as const
export type JourneyPurpose = (typeof JOURNEY_PURPOSES)[number]
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')

export const JourneyInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  name: z.string().min(1),
  purpose: z.string().refine((v) => (JOURNEY_PURPOSES as readonly string[]).includes(v), { message: `The purpose must be one of: ${JOURNEY_PURPOSES.join(', ')}` }),
  /** An event (record.created, record.updated, record.saved, payment.failed...) or a date on a record (journeys.ts). */
  trigger: JourneyTrigger,
  steps: z.array(JourneyStep).min(1),
  stop_rules: z.array(z.enum(['customer_replied', 'resolved', 'opted_out', 'wrong_person', 'record_changed'])).default(['customer_replied', 'resolved', 'opted_out', 'wrong_person']),
  quiet_hours: z.object({ start: HHMM, end: HHMM }).default({ start: '20:00', end: '08:00' }),
  contact_cap: z.object({ per_days: z.number().int().positive(), max: z.number().int().positive() }).default({ per_days: 7, max: 3 }),
  mode: z.enum(['off', 'shadow', 'dry_run', 'live']).default('shadow'),
  /** The staff member who answers for this journey: shown in Studio, named and told in its alarms (G 16.6-10). */
  owner_id: z.string().uuid().optional(),
  /**
   * What a customer reply does. `stop` (the default, also implied by the `customer_replied` stop rule) ends
   * the run. `chase` pauses it while a person or the AI is talking with the customer, and resumes the
   * cadence once the customer has been silent for `silence_hours`, from `resume_step` (a recovery cadence).
   */
  reply: z.object({
    mode: z.enum(['stop', 'chase']),
    silence_hours: z.number().positive().max(24 * 30).default(23),
    resume_step: z.number().int().nonnegative().optional(),
  }).optional(),
  /** The outcome the journey is for: when the record matches any of these, the run completes (goal reached). */
  goal: z.array(z.object({ field: z.string(), in: z.array(z.unknown()).min(1) })).optional(),
  /** Decision questions that end the run when they fire on the customer's messages (complaint, vulnerability...). */
  stop_on: z.array(z.string()).optional(),
  /** How long a step may wait for delivery truth before the run is paused and an alarm raised. */
  delivery_wait_hours: z.number().positive().max(24 * 14).optional(),
  /** When every step was sent and the goal was not reached: nothing, or an exception for a person (e.g. collections). */
  on_complete: z.enum(['none', 'exception']).optional(),
  /** Transactional or journey, computed values, the wait after the last step, SMS opt-out wording (journeys.ts). */
  ...JourneyOptions.shape,
}).refine((j) => !(j.reply?.mode === 'chase' && j.stop_rules.includes('customer_replied')), {
  message: 'A journey that chases after a reply cannot also stop when the customer replies', path: ['stop_rules'],
}).refine((j) => j.reply?.resume_step === undefined || j.reply.resume_step < j.steps.length, {
  message: 'reply.resume_step must be one of the steps', path: ['reply', 'resume_step'],
}).refine((j) => !(j.values ?? []).some((v, i, all) => all.findIndex((w) => w.key === v.key) !== i), {
  message: 'Two computed values have the same key', path: ['values'],
})
export type JourneyInput = z.infer<typeof JourneyInput>

export const MessageTemplateInput = z.object({
  key: z.string(),
  channel: z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp', 'any']),
  subject: z.string().optional(),
  body: z.string().min(1),
  regulated: z.boolean().default(false),
  /** Email only: the inbox preview text after the subject (the preheader). */
  preheader: z.string().max(300).optional(),
  /** Email only: a large headline above the body (a hero headline). */
  headline: z.string().max(300).optional(),
})
export type MessageTemplateInput = z.infer<typeof MessageTemplateInput>

// --- Claims: a request opened against one of the customer's records (services/claims.ts) ----------------
/**
 * How the `create_claim` action and website claim forms open a claim: a record of `object`, about one thing the
 * customer names (`item`), optionally against one of their own records (`against`: a plan, a contract, an order),
 * checked against the records that say what that parent covers (`coverage`). Every key and word is the company's own;
 * the platform assumes none of them.
 */
export const ClaimSettings = z.object({
  /** The object a claim is a record of. */
  object: z.string().min(1).default('claim'),
  /** The prefix of claim numbers (one counter serves the AI's action and the website form). */
  number_prefix: z.string().regex(/^[A-Z]{1,6}$/).default('CL'),
  /** Status values that mean a claim is finished (anything else counts as open). */
  closed_statuses: z.array(z.string().min(1)).default(['closed', 'resolved', 'rejected', 'cancelled', 'completed']),
  /** What the claim is about: the claim field it is written to, and the word used for it in messages and summaries. */
  item: z.object({
    field: z.string().min(1).default('item'),
    label: z.string().min(1).default('item'),
    /** An example value for the AI's tool description, e.g. "Laptop". */
    example: z.string().max(80).default(''),
  }).prefault({}),
  /** The claim field for what is wrong, and the field for when it started (YYYY-MM-DD). */
  problem_field: z.string().min(1).default('description'),
  started_field: z.string().min(1).default('started_on'),
  /** The customer's record the claim is opened against; null: claims are not tied to another record. */
  against: z.object({
    object: z.string().min(1),
    /** The parent's own reference field (its title is used when the field is empty). */
    reference_field: z.string().min(1).default('number'),
    /** The claim field the parent's reference is written to (also the action's argument for choosing one). */
    claim_field: z.string().min(1),
    /** The parent's status field and the values that allow a claim; other values send the claim to a person. */
    status_field: z.string().min(1).default('status'),
    active_values: z.array(z.string().min(1)).min(1).default(['active']),
    /** The word for the parent in messages, e.g. "plan", "contract", "order". */
    label: z.string().min(1).default('record'),
  }).nullable().default(null),
  /**
   * The records that list what each parent covers: a record of `object` whose `reference_field` holds the parent's
   * reference and whose `item_field` names a covered item. When a parent has any, the claimed item must match one.
   */
  coverage: z.object({
    object: z.string().min(1),
    reference_field: z.string().min(1),
    item_field: z.string().min(1),
  }).nullable().default(null),
  /** One open claim per item (and parent): asking again returns the claim already open. */
  one_open_per_item: z.boolean().default(true),
})
export type ClaimSettings = z.infer<typeof ClaimSettings>

// --- Quote records (services/quoteBuilder.ts) -------------------------------------------------------------
/**
 * Where a quote the AI builds is mirrored as one of the company's own records, and which of its fields take the
 * computed values. Each value is written only to the fields the object has. `tag_counts`: per product tag, the
 * field that takes how many quoted items carry the tag (a boolean field takes whether any do).
 */
export const QuoteRecordSettings = z.object({
  object: z.string().min(1).default('quote'),
  /** How many days a quote the AI builds is held. */
  valid_days: z.number().int().min(1).max(365).default(30),
  fields: z.object({
    product: z.array(z.string().min(1)).default(['product']),
    quantity: z.array(z.string().min(1)).default(['quantity']),
    total: z.array(z.string().min(1)).default(['total_minor', 'amount_minor']),
    valid_until: z.array(z.string().min(1)).default(['valid_until']),
    details: z.array(z.string().min(1)).default(['details']),
    summary: z.array(z.string().min(1)).default(['line_summary']),
  }).prefault({}),
  tag_counts: z.array(z.object({ tag: z.string().trim().toLowerCase().min(1).max(40), field: z.string().min(1) })).max(10).default([]),
})
export type QuoteRecordSettings = z.infer<typeof QuoteRecordSettings>

// --- Company settings (design 15.3) -----------------------------------------
export const CompanySettings = z.object({
  limits: z.object({
    refund_minor: z.number().int().nonnegative().default(0),
    waive_fee_minor: z.number().int().nonnegative().default(0),
    discount_percent: z.number().nonnegative().default(0),
  }).default({ refund_minor: 0, waive_fee_minor: 0, discount_percent: 0 }),
  reply_times: z.object({                         // promised reply times when a person is needed
    staffed_minutes: z.number().int().positive().default(60),
    unstaffed: z.enum(['next_staffed_start', 'next_staffed_plus_hours']).default('next_staffed_start'),
  }).default({ staffed_minutes: 60, unstaffed: 'next_staffed_start' }),
  /**
   * Holding replies on email and SMS holds (a company may say nothing until a reviewer acts on mail they will probably
   * ignore). A hold whose reason code is listed, or whose priority is `skip_priority_from` or lower (a larger number),
   * gets no holding reply and so no promised time. Never skipped: a request for a person, priority 1, live chat and
   * calls (the customer is waiting there and then).
   */
  holding_replies: z.object({
    skip_reason_codes: z.array(z.string().min(1)).default([]),
    skip_priority_from: z.number().int().min(2).max(5).nullable().default(null),
  }).default({ skip_reason_codes: [], skip_priority_from: null }),
  hours: z.record(z.string(), z.array(z.tuple([z.string(), z.string()]))).default({
    mon: [['09:00', '17:30']], tue: [['09:00', '17:30']], wed: [['09:00', '17:30']], thu: [['09:00', '17:30']], fri: [['09:00', '17:30']],
  }),
  quiet_hours: z.object({ start: z.string(), end: z.string() }).default({ start: '20:00', end: '08:00' }),
  retention_days: z.number().int().positive().default(2555),
  keep_agent_transcripts: z.boolean().default(false),
  allow_code_leave_region: z.boolean().default(false),
  own_subscription_mode: z.boolean().default(false),
  /**
   * Agent progress steps the customer sees (design 10.9): never, or the steps an agent marks for the customer,
   * sent through the send gate at most once every few hours per task.
   */
  customer_progress_updates: z.enum(['off', 'marked_steps']).default('off'),
  /**
   * Chargeable task work (design 10.4): a template's price in credits is drawn from the customer's retainer
   * (`credit_hours` retainer hours per credit). Work the retainer does not cover is quoted to the customer at
   * `credit_price_minor` per credit, in the company's currency, and paid through a payment link before it
   * starts when `require_payment` is on. With no price set, a consultant prices the quote.
   */
  task_quotes: z.object({
    credit_price_minor: z.number().int().nonnegative().default(0),
    credit_hours: z.number().positive().default(1),
    require_payment: z.boolean().default(true),
  }).default({ credit_price_minor: 0, credit_hours: 1, require_payment: true }),
  confidence_floor: z.number().min(0).max(1).default(0.7),
  resolve_close_days: z.number().int().positive().default(7),
  clarification_max_rounds: z.number().int().positive().default(2),
  audit_sample_rate: z.number().min(0).max(1).default(0.05),
  error_budget_per_week: z.number().int().nonnegative().default(2),
  jev_languages: z.array(z.string()).default(['english']),
  allowed_link_domains: z.array(z.string()).default([]),
  banned_phrases: z.array(z.string()).default([]),
  /**
   * Where live availability comes from ("never claim booking availability" without a live source). `crm`: session records
   * (any object with starts_at and capacity) read by check_availability. `none`: the company keeps no availability
   * the AI can see, so the tool is not offered and drafts that say a time is or is not free are held.
   */
  availability_source: z.enum(['crm', 'none']).default('crm'),
  /**
   * The level a request type starts at on a channel the first time it is seen (design 13.1: shadow, 0). A company
   * that already sends clean mail at once (no automation rules; the gating questions do the holding) can start
   * new types higher on that channel. Locked platform questions, checks and the confidence floor still hold.
   */
  autonomy_start: z.partialRecord(z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp']), z.number().int().min(0).max(3)).default({}),
  /** The drafting pad: the company's own note per platform (facebook, instagram, google_review...). */
  draft_pad: z.object({
    platform_notes: z.record(z.string().regex(/^[a-z_]{2,30}$/), z.string().min(1).max(600)).default({}),
  }).default({ platform_notes: {} }),
  /**
   * What the AI may promise on its own (design 8.6 "only allowed promises"). A promise of another kind, or due
   * later than `max_hours`, holds the reply for a person. `needs_action`: only with a matching approved action
   * (a refund promise needs a refund the policy allows).
   */
  allowed_promises: z.array(z.object({
    kind: z.enum(['callback', 'send_item', 'refund', 'follow_up', 'other']),
    max_hours: z.number().positive().max(24 * 90).optional(),
    needs_action: z.string().optional(),
  })).default([
    { kind: 'follow_up', max_hours: 72 },
    { kind: 'callback', max_hours: 48 },
    { kind: 'send_item', max_hours: 72 },
    { kind: 'refund', max_hours: 24 * 10, needs_action: 'refund' },
  ]),
  /**
   * Commitments in sent replies (services/commitments.ts). Every specific, unconditional commitment
   * a sent reply makes becomes a tracked promise. One with no time ("our team is looking into this") is due
   * `untimed_staffed_hours` of staffed time after it was made. Each reply with commitments is emailed to `notify`
   * (a monitoring list); when that is empty, to the email channels' monitoring copies (BCC).
   */
  commitments: z.object({
    untimed_staffed_hours: z.number().int().min(1).max(80).default(8),
    notify: z.array(z.string().trim().toLowerCase().email()).max(10).default([]),
    email_monitoring: z.boolean().default(true),
  }).default({ untimed_staffed_hours: 8, notify: [], email_monitoring: true }),
  /** Platform and industry-pack updates the company has not customised are applied automatically (design 8.4, 9.1). */
  library_auto_update: z.boolean().default(true),
  voice: z.object({
    recording_notice: z.boolean().default(true),
    transfer_to: z.string().nullable().default(null),          // E.164 or sip: URI for warm transfer in staffed hours
    max_call_minutes: z.number().int().positive().default(15),
    fallback_to: z.string().nullable().default(null),         // the company's own line if GPT-Live is unavailable
  }).default({ recording_notice: true, transfer_to: null, max_call_minutes: 15, fallback_to: null }),
  /** SMS and chat: keywords, opt-out wording, unknown senders, delivery feedback, callbacks (messaging.ts). */
  messaging: MessagingSettings.default(MessagingSettings.parse({})),
  /** The review desk. */
  review: z.object({
    /** A held item is released after this long with nobody active on it. */
    hold_idle_minutes: z.number().int().min(2).max(240).default(20),
    /** "Spam" decided below this probability goes to a person instead of being ignored. */
    spam_review_below: z.number().min(0.5).max(1).default(0.75),
    /**
     * Batches: items where one of these decision questions fired are never batched; each needs a person's
     * own judgement. The platform's complaint, frustration, vulnerability, legal, safety and wants-a-person questions
     * are always left out, as is any reply that carries a refund or other money action.
     */
    batch_exclude_questions: z.array(z.string().min(1)).max(50).default([]),
    /** One-key reasons for "No reply needed", the company's own words (none by default: a reason is typed). */
    no_reply_presets: z.array(z.string().trim().min(1).max(80)).max(9).default([]),
    /** Opening an item claims it softly; the claim lapses this long after the person's last activity. */
    presence_ttl_seconds: z.number().int().min(30).max(1800).default(180),
    /** A veto-window AI reply waits at most this long while someone has its conversation open, then goes to the desk. */
    presence_defer_minutes: z.number().int().min(1).max(240).default(30),
    /**
     * The coverage warning: over `days`, at least `min_items` items needing a reply on a channel and `share` or more
     * closed with "No reply needed". `enabled: false` turns it off for the company.
     */
    coverage: z.object({
      enabled: z.boolean().default(true),
      days: z.number().int().min(1).max(90).default(30),
      min_items: z.number().int().min(1).max(10_000).default(10),
      share: z.number().min(0.5).max(1).default(0.95),
    }).default({ enabled: true, days: 30, min_items: 10, share: 0.95 }),
  }).default({ hold_idle_minutes: 20, spam_review_below: 0.75, batch_exclude_questions: [], no_reply_presets: [], presence_ttl_seconds: 180, presence_defer_minutes: 30, coverage: { enabled: true, days: 30, min_items: 10, share: 0.95 } }),
  autonomy: AutonomyPolicy.prefault({}),                       // how autonomy is earned and lost (design 13.1, 13.4)
  /**
   * This company's own model route per purpose (design 8.2; C 8.2-02), chosen by comparing models on its evals and
   * released as an approved change set. `vendor:model`, tried in order. Unset purposes use the deployment's route
   * (MODEL_ROUTES) or the default (GPT-6 Luna; GPT-6 Astra for task verification).
   */
  model_routes: z.record(z.string().regex(/^[a-z_]+$/), z.array(z.string().regex(/^(openai|anthropic):[A-Za-z0-9._-]+$/)).min(1).max(4)).default({}),
  /** Opening claims against the customer's records (ClaimSettings). */
  claims: ClaimSettings.prefault({}),
  /** The company's own quote records, written when the AI builds a quote (QuoteRecordSettings). */
  quote_records: QuoteRecordSettings.prefault({}),
  /**
   * Asking for a rating after resolution (design 18.11 "Customer rating"; services/ratings.ts). A short "How did we
   * do? Reply 1 to 5" goes through the send gate `delay_minutes` after a conversation is resolved, only to a customer
   * who agreed to feedback requests on that channel (consent purpose `feedback`), at most once per `cooldown_days`.
   * A reply that is just a score within `answer_hours` is recorded and does not reopen the conversation.
   */
  ratings: z.object({
    ask_after_resolution: z.boolean().default(true),
    channels: z.array(z.enum(['email', 'sms'])).default(['email', 'sms']),
    delay_minutes: z.number().int().min(5).max(7 * 24 * 60).default(60),
    cooldown_days: z.number().int().min(0).max(365).default(30),
    answer_hours: z.number().int().min(1).max(14 * 24).default(72),
  }).default({ ask_after_resolution: true, channels: ['email', 'sms'], delay_minutes: 60, cooldown_days: 30, answer_hours: 72 }),
})
export type CompanySettings = z.infer<typeof CompanySettings>

export const Brand = z.object({
  assistant_name: z.string().default('Assistant'),
  tone: z.string().default('Warm, clear and concise.'),
  signature: z.string().default(''),
  greeting: z.string().default('Hi, how can I help?'),
  primary_color: z.string().default('#4338ca'),
  logo_url: z.string().optional(),
  voice: z.string().default('marin'),
  disclosure: z.string().default("I'm an AI assistant."),
  /** House rules for every reply, stated to the AI as rules (writing rules that are not facts). */
  house_rules: z.array(z.string().min(1)).default([]),
  /** The company's spelling; drafts that break it are held. */
  spelling: z.enum(['en-GB', 'en-AU', 'en-US']).nullable().default(null),
  /** Names that must keep their exact casing (for example "ExampleCo"). */
  brand_terms: z.array(z.string().min(1)).default([]),
  /**
   * Conversation checks a company can turn on: the AI is told the rule, and a draft that breaks it is written again
   * once, then held for a person. `no_repeat`: never say again what one of our last three messages said.
   * `not_as_customer`: never write a line as the customer or invent their answer.
   */
  conversation_checks: z.object({ no_repeat: z.boolean().default(false), not_as_customer: z.boolean().default(false) }).default({ no_repeat: false, not_as_customer: false }),
  /**
   * The AI notice under every AI email (an editable disclaimer). Empty: the built-in line
   * ("<assistant> is <company>'s AI assistant. Reply "person"..."), in the customer's language. Set: this text
   * replaces it, in the company's language; `{assistant}` and `{company}` are filled in. An email channel can
   * set its own (template.ai_disclaimer), and each language its own (languages.<code>.ai_footer).
   */
  ai_footer: z.string().trim().max(1000).nullable().default(null),
  /**
   * The brand voice per language (ISO 639-1 code, A 5.2-08): when a customer writes in that language, these replace
   * the defaults above. Anything not given is written by the AI in the customer's language from the defaults.
   */
  languages: z.record(z.string().regex(/^[a-z]{2,3}$/, 'Use a language code such as fr or zh'), z.object({
    tone: z.string().optional(),
    greeting: z.string().optional(),
    disclosure: z.string().optional(),
    signature: z.string().optional(),
    ai_footer: z.string().max(1000).optional(),
  })).optional(),
})
export type Brand = z.infer<typeof Brand>

// --- Change sets (design 15.1, 15.2) ----------------------------------------
export const ChangeEntity = z.enum([
  'decision_question', 'playbook', 'knowledge', 'task_template', 'journey', 'message_template',
  'object_type', 'autonomy', 'company_settings', 'brand',
])
export type ChangeEntity = z.infer<typeof ChangeEntity>

export const Change = z.object({
  entity: ChangeEntity,
  op: z.enum(['create', 'update', 'delete']),
  key: z.string(),                 // entity key (knowledge uses its id or a new slug)
  after: z.unknown().optional(),   // full new value for create/update
  before: z.unknown().optional(),  // filled in by the server when the draft is made
  summary: z.string().default(''),
})
export type Change = z.infer<typeof Change>

export const ENTITY_SCHEMAS = {
  decision_question: DecisionQuestionInput,
  playbook: PlaybookInput,
  knowledge: KnowledgeInput,
  task_template: TaskTemplateInput,
  journey: JourneyInput,
  message_template: MessageTemplateInput,
  object_type: ObjectTypeInput,
  autonomy: z.object({
    subject_kind: z.enum(['request_type', 'task_template']), subject_key: z.string(), channel: z.string(), level: z.number().int().min(0).max(3),
    veto_minutes: z.number().int().min(1).max(24 * 60).optional(), max_level: z.number().int().min(0).max(3).optional(), locked_reason: z.string().max(300).nullish(),
  }),
  company_settings: CompanySettings,
  brand: Brand,
} as const

